import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { binaryAPI, arbitrageAPI } from '../../services/api';
import MiniTradeTracker from './MiniTradeTracker';
import TradeReportDialog from './TradeReportDialog';

export const TRADE_SETTLED = 'phantomtrades:trade-settled';
export const TRADE_PLACED = 'phantomtrades:trade-placed';
export const OPEN_HISTORY = 'phantomtrades:open-history';

const ended = (t) => new Date(t.end_time).getTime() <= Date.now();

const findReport = async (tradeId, kind) => {
  for (let attempt = 0; attempt < 3; attempt += 1) {
    const list = kind === 'arbitrage'
      ? await arbitrageAPI.getInvestmentHistory().catch(() => [])
      : await binaryAPI.getTradeHistory(20).catch(() => []);
    const hit = list.find((t) => t.id === tradeId);
    if (hit) return hit.status === 'cancelled' ? null : { ...hit, kind };
    await new Promise((r) => setTimeout(r, 1500));
  }
  return null;
};

// App-wide: tracks running trades on every page, settles them when time is up,
// and pops a result report that links to History.
export const TradeWatcher = () => {
  const [binary, setBinary] = useState([]);
  const [arbitrage, setArbitrage] = useState([]);
  const [queue, setQueue] = useState([]);
  const settlingRef = useRef(new Set());
  const reportedRef = useRef(new Set());
  const navigate = useNavigate();
  const location = useLocation();

  const prevIdsRef = useRef({ binary: null, arbitrage: null });

  // Trades that vanish from the active list were settled elsewhere (server sweep, other tab)
  const track = useCallback((kind, list) => {
    const prev = prevIdsRef.current[kind];
    const ids = new Set(list.map((t) => t.id));
    if (prev) {
      prev.forEach((id) => {
        if (!ids.has(id)) window.dispatchEvent(new CustomEvent(TRADE_SETTLED, { detail: { trade_id: id, trade_type: kind } }));
      });
    }
    prevIdsRef.current[kind] = ids;
  }, []);

  const loadBinary = useCallback(async () => {
    const list = await binaryAPI.getActiveTrades().catch(() => null);
    if (!list) return;
    track('binary', list);
    setBinary(list.map((t) => ({ ...t, kind: 'binary' })));
  }, [track]);
  const loadArbitrage = useCallback(async () => {
    const list = await arbitrageAPI.getActiveInvestments().catch(() => null);
    if (!list) return;
    track('arbitrage', list);
    setArbitrage(list.map((t) => ({ ...t, kind: 'arbitrage' })));
  }, [track]);

  useEffect(() => { loadBinary(); loadArbitrage(); }, [loadBinary, loadArbitrage, location.pathname]);

  useEffect(() => {
    const iv = setInterval(loadBinary, binary.length ? 4000 : 20000);
    return () => clearInterval(iv);
  }, [binary.length, loadBinary]);

  useEffect(() => {
    const iv = setInterval(loadArbitrage, 60000);
    return () => clearInterval(iv);
  }, [loadArbitrage]);

  useEffect(() => {
    const onPlaced = () => { loadBinary(); loadArbitrage(); };
    const onSettled = async (e) => {
      const { trade_id: id, trade_type: type } = e.detail || {};
      if (!id || reportedRef.current.has(id)) return;
      reportedRef.current.add(id);
      setBinary((l) => l.filter((t) => t.id !== id));
      setArbitrage((l) => l.filter((t) => t.id !== id));
      const report = await findReport(id, type === 'arbitrage' ? 'arbitrage' : 'binary');
      if (report) setQueue((q) => [...q, report]);
    };
    window.addEventListener(TRADE_PLACED, onPlaced);
    window.addEventListener(TRADE_SETTLED, onSettled);
    return () => {
      window.removeEventListener(TRADE_PLACED, onPlaced);
      window.removeEventListener(TRADE_SETTLED, onSettled);
    };
  }, [loadBinary, loadArbitrage]);

  // Settle anything whose countdown has finished
  useEffect(() => {
    const iv = setInterval(() => {
      [...binary, ...arbitrage].filter((t) => ended(t) && !settlingRef.current.has(t.id)).forEach(async (t) => {
        settlingRef.current.add(t.id);
        const settle = t.kind === 'binary' ? binaryAPI.settleTrade : arbitrageAPI.settleInvestment;
        await settle(t.id).catch(() => null);
        window.dispatchEvent(new CustomEvent(TRADE_SETTLED, { detail: { trade_id: t.id, trade_type: t.kind } }));
        window.dispatchEvent(new CustomEvent('phantomtrades:balance-changed', { detail: {} }));
      });
    }, 1000);
    return () => clearInterval(iv);
  }, [binary, arbitrage]);

  const closeReport = () => setQueue((q) => q.slice(1));
  const viewHistory = () => {
    setQueue([]);
    navigate('/trading?tab=history');
    window.dispatchEvent(new Event(OPEN_HISTORY));
  };

  const running = [...binary, ...arbitrage].filter((t) => !settlingRef.current.has(t.id));
  return (
    <>
      {location.pathname !== '/trading' && <MiniTradeTracker trades={running} />}
      <TradeReportDialog report={queue[0]} onClose={closeReport} onViewHistory={viewHistory} />
    </>
  );
};

export default TradeWatcher;
